/**
 * Ticket 73 — the pure half of gifting subs to a room's host.
 *
 * Kept out of the components so the money rules can be tested without a DOM:
 * every one of them is about not lying to somebody who is about to be charged.
 */

export interface GiftSubPackage {
  key: string;
  quantity: number;
  amount_cents: number;
}

/** The two points of sale. */
export type GiftSubProvider = 'stripe' | 'privcart';

export interface GiftSubCatalogue {
  enabled: boolean;
  currency: string;
  unit_price_cents: number;
  packages: GiftSubPackage[];
  // Added when the crypto rail landed. Optional on purpose: a response from an
  // older server carries none of these, and the menu must degrade to the card
  // path rather than rendering an empty chooser.
  stripe_available?: boolean;
  crypto_available?: boolean;
  crypto_assets?: string[];
}

export interface GiftSubCheckout {
  checkout_url: string;
  purchase_id: number;
  provider?: GiftSubProvider;
  asset?: string;
  amount?: string;
  expires_at?: string | null;
}

/**
 * How each rail is described to the buyer.
 *
 * The crypto label names no specific coin: which coins are on offer is decided
 * by the server (see `cryptoAssets` below), and a hardcoded name here would go
 * stale the moment that list changed.
 */
export const PROVIDER_LABELS: Record<GiftSubProvider, string> = {
  stripe: 'Credit or debit card',
  privcart: 'Cryptocurrency',
};

export const PROVIDER_HINTS: Record<GiftSubProvider, string> = {
  stripe:
    'Pay by card on Stripe. You will get an invoice and can follow the steps there.',
  privcart:
    'Pay on-chain in crypto. We generate an invoice with a QR code and address.',
};

/**
 * Cents to a displayed price.
 *
 * Whole dollars render without the trailing `.00` ("$20", not "$20.00") because
 * every package in the catalogue is a whole number of dollars and the zeroes
 * are noise — but a price that is NOT whole must still show its cents, or a
 * future $4.99 tier would advertise itself as $4.
 */
export const formatPrice = (cents: number, currency = 'usd'): string => {
  const symbol = currency.toLowerCase() === 'usd' ? '$' : '';
  const negative = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const whole = Math.floor(abs / 100);
  const remainder = abs % 100;
  const body =
    remainder === 0
      ? `${whole}`
      : `${whole}.${remainder.toString().padStart(2, '0')}`;

  return `${negative ? '-' : ''}${symbol}${body}`;
};

/** "Gift 1 Sub" / "Gift 5 Subs". */
export const packageLabel = (quantity: number): string =>
  `Gift ${quantity} ${quantity === 1 ? 'Sub' : 'Subs'}`;

/**
 * Whether the menu may be offered to this viewer at all.
 *
 * Nobody gifts themselves, and a room that has ended takes no money — the
 * server refuses both, and this keeps the button from offering a purchase that
 * is going to be rejected after the buyer has committed to it.
 */
export const canGift = (
  roomState: string,
  hostAccountId: string,
  viewerAccountId: string | null | undefined,
  enabled: boolean | undefined,
): boolean =>
  enabled === true &&
  roomState === 'live' &&
  !!viewerAccountId &&
  viewerAccountId !== hostAccountId;

/**
 * A price the SERVER quoted, or nothing.
 *
 * The catalogue is fetched rather than hardcoded so the button and the charge
 * cannot drift apart; if the fetch fails we render no prices rather than a
 * remembered set that may no longer be what Stripe will bill.
 */
export const isUsableCatalogue = (
  catalogue: GiftSubCatalogue | null,
): catalogue is GiftSubCatalogue =>
  !!catalogue &&
  Array.isArray(catalogue.packages) &&
  catalogue.packages.length > 0 &&
  catalogue.packages.every(
    (p) =>
      typeof p.key === 'string' &&
      Number.isFinite(p.quantity) &&
      p.quantity > 0 &&
      Number.isFinite(p.amount_cents) &&
      p.amount_cents > 0,
  );

/**
 * Which rails this buyer may actually be offered.
 *
 * Both flags come from the server, because only the server knows whether this
 * box holds credentials for a processor. A menu that offers a rail we cannot
 * reach sends the buyer into an error after they have committed to paying.
 *
 * An OLDER server sends neither flag. That is read as card-only rather than as
 * nothing available, because the card path predates the flag and refusing it
 * would break gifting outright against a server mid-deploy.
 */
export const availableProviders = (
  catalogue: GiftSubCatalogue | null,
): GiftSubProvider[] => {
  if (!catalogue) return [];

  const providers: GiftSubProvider[] = [];

  if (catalogue.stripe_available !== false) providers.push('stripe');
  if (catalogue.crypto_available === true) providers.push('privcart');

  return providers;
};

/**
 * The coins a buyer may choose between, in the order the server offers them.
 *
 * Server-supplied, never hardcoded, and for a sharper reason than usual: a
 * PrivCart invoice has no currency field, so the amount is denominated in the
 * asset itself. The dollar price is converted server-side at the rate quoted
 * when the invoice is minted, so this list must never be reordered or extended
 * here — the first entry is the default, and only the server knows which coins
 * it can actually price.
 */
export const cryptoAssets = (catalogue: GiftSubCatalogue | null): string[] =>
  Array.isArray(catalogue?.crypto_assets)
    ? catalogue.crypto_assets.filter(
        (a) => typeof a === 'string' && a.length > 0 && a.length <= 10,
      )
    : [];

/**
 * Full names for the coins, so a buyer picking a payment method is not asked to
 * recognise a three-letter ticker.
 *
 * A ticker with no entry here still renders — as itself — because the list is
 * server-supplied and must not silently drop a coin this build has not been
 * told about.
 */
export const ASSET_NAMES: Record<string, string> = {
  XMR: 'Monero',
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  SOL: 'Solana',
  LTC: 'Litecoin',
};

export const assetLabel = (asset: string): string => {
  const name = ASSET_NAMES[asset.toUpperCase()];

  return name ? `${name} (${asset.toUpperCase()})` : asset.toUpperCase();
};

/**
 * Whether the chooser is worth showing at all.
 *
 * With one rail available there is nothing to choose, so the step is skipped
 * and the buyer goes straight to it. A menu that asks a question with one
 * possible answer is a click that buys the buyer nothing.
 */
export const needsProviderChoice = (
  catalogue: GiftSubCatalogue | null,
): boolean => availableProviders(catalogue).length > 1;
